import React from "react"

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Clock, Trophy, BookOpen } from "lucide-react"
import { useTranslation } from "../contexts/translation-context"
import type { Exam } from "../types/quiz"

interface ExamCardProps {
  exam: Exam
  isCompleted: boolean
  onStart: (exam: Exam) => void
}

export function ExamCard({ exam, isCompleted, onStart }: ExamCardProps) {
  const { t } = useTranslation()

  return (
    <Card className="w-full bg-white shadow-sm border border-gray-200 hover:shadow-md transition-all duration-200 rounded-xl">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <CardTitle className="text-lg leading-tight text-gray-900">{exam.title}</CardTitle>
            <CardDescription className="text-sm text-gray-600">
              {t(`subjects.${exam.subject}`) || exam.subject} • {exam.year} •{" "}
              {t(`grades.${exam.grade}`) || `${exam.grade}th Grade`}
            </CardDescription>
          </div>
          {isCompleted && (
            <Badge variant="secondary" className="ml-2 bg-secondary-50 text-secondary border-secondary rounded-lg">
              <Trophy className="w-3 h-3 mr-1" />
              {t("quiz.completed")}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <div className="flex items-center gap-4 text-sm text-gray-600 mb-4">
          <div className="flex items-center gap-1">
            <BookOpen className="w-4 h-4" />
            {exam.questions.length} {t("quiz.questions")}
          </div>
          <div className="flex items-center gap-1">
            <Trophy className="w-4 h-4 text-secondary" />
            {exam.totalPoints} {t("quiz.points")}
          </div>
          {exam.duration && (
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {exam.duration} {t("quiz.min")}
            </div>
          )}
        </div>
        <Button
          onClick={() => onStart(exam)}
          className={`w-full rounded-xl font-medium transition-all duration-200 ${isCompleted
              ? "bg-white text-primary border-primary hover:bg-primary-50 border"
              : "bg-primary hover:bg-primary-600 text-white"
            }`}
          variant={isCompleted ? "outline" : "default"}
        >
          {isCompleted ? t("quiz.retakeExam") : t("quiz.startExam")}
        </Button>
      </CardContent>
    </Card>
  )
}
